import type { PropsWithChildren } from 'react';
import React, { useState } from 'react';
import get from 'lodash/get';

import type { TAccountProduct } from '../../common';
import {
  BackText,
  Loading,
  PrimaryAltIconButton,
  SecondaryTransparentButton
} from '../atoms';
import { ProcessingModal } from '../molecules';
import { EditableTable } from '../organisms/EditableTable';

const ProductMatrixWrapper = ({
  children,
  title,
  product,
  removeFeeConditions,
  feeId
}: PropsWithChildren<{
  title: string;
  product: TAccountProduct;
  removeFeeConditions: (id: FEE_IDS) => Promise<TAccountProduct>;
  feeId: FEE_IDS;
}>) => {
  const [processedMsg, setProcessedMsg] = useState('');
  const [processingModalOpen, setProcessingModalOpen] = useState(false);
  const [removingConditions, setRemovingConditions] = useState(false);
  const [showConditions, setShowConditions] = useState(
    !!get(product, `fees.${feeId}.conditions.terms.length`, 0)
  );
  let conditions = null;
  let initial = null;

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      switch (child.type) {
        case ProductMatrixWrapper.Conditions:
          conditions = child;
          break;
        case ProductMatrixWrapper.Initial:
          initial = child;
          break;
        default:
          break;
      }
    }
  });

  const revertConditions = async () => {
    try {
      setRemovingConditions(true);
      await removeFeeConditions(feeId);
      setShowConditions(!showConditions);
      setProcessedMsg('Successfully removed conditions');
    } catch (e) {
      console.error(e);
      setProcessedMsg('Something went wrong while trying to remove conditions');
    } finally {
      setRemovingConditions(false);
    }
  };

  return (
    <div>
      <ProcessingModal
        isModalOpen={processingModalOpen}
        handleModalClose={() => setProcessingModalOpen(false)}
        processing={removingConditions}
        title="Remove Conditions"
        processedMsg={processedMsg}
        confirmation={true}
        confirmationMessage={
          'Are you sure you want to revert to the Simple Structure? Doing so will remove all data for Conditions.'
        }
        handleConfirmation={revertConditions}
      />
      {!showConditions && (
        <EditableTable.PreHeader>
          <h2>{title}</h2>
          {conditions && (
            <span>
              <PrimaryAltIconButton
                type="button"
                onClick={() => setShowConditions(!showConditions)}
              >
                <span>Add Conditions</span>
                <span className="material-symbols-rounded">add</span>
              </PrimaryAltIconButton>
            </span>
          )}
        </EditableTable.PreHeader>
      )}
      {showConditions && (
        <EditableTable.PreHeader
          style={{
            display: 'flex',
            justifyContent: 'end',
            marginTop: '-1.75rem',
            marginBottom: '-0.75rem'
          }}
        >
          <SecondaryTransparentButton
            type={'button'}
            style={{ position: removingConditions ? 'relative' : 'static' }}
            onClick={() => setProcessingModalOpen(true)}
          >
            {removingConditions && (
              <Loading
                style={{
                  height: '16px',
                  width: '16px',
                  borderWidth: '2px',
                  display: 'inline-block',
                  position: 'absolute',
                  top: '5px',
                  left: '45%'
                }}
              />
            )}
            <BackText text="Remove Conditions" />
          </SecondaryTransparentButton>
        </EditableTable.PreHeader>
      )}

      {showConditions ? conditions : initial}
    </div>
  );
};
ProductMatrixWrapper.Conditions = ({ children }: PropsWithChildren) => (
  <div>{children}</div>
);
ProductMatrixWrapper.Initial = ({ children }: PropsWithChildren) => (
  <div>{children}</div>
);

export { ProductMatrixWrapper };
